import { Ng2StateDeclaration, UIView } from '@uirouter/angular';

import {
  assetsRedirectHandler,
  bankingRedirectHandler,
  borrowersRedirectHandler,
  incomeRedirectHandler,
  propertiesRedirectHandler,
  referralRedirectHandler,
} from './slides-redirect-handlers';

export class SlidesStateNames {
  static readonly SlidesParent = 'client.journey.slides';
  static readonly GettingStarted = `${this.SlidesParent}.gettingStarted`;

  static readonly Borrowers = `${this.SlidesParent}.borrowers`;
  static readonly BorrowersEdit = `${this.Borrowers}.edit`;
  static readonly BorrowersSummary = `${this.Borrowers}.summary`;

  static readonly Income = `${this.SlidesParent}.income`;
  static readonly IncomeEdit = `${this.Income}.edit`;
  static readonly IncomeSummary = `${this.Income}.summary`;

  static readonly Banking = `${this.SlidesParent}.banking`;
  static readonly BankingEdit = `${this.Banking}.edit`;
  static readonly BankingSummary = `${this.Banking}.summary`;

  static readonly Assets = `${this.SlidesParent}.assets`;
  static readonly AssetsEdit = `${this.Assets}.edit`;
  static readonly AssetsSummary = `${this.Assets}.summary`;

  static readonly Properties = `${this.SlidesParent}.properties`;
  static readonly PropertiesEdit = `${this.Properties}.edit`;
  static readonly PropertiesSummary = `${this.Properties}.summary`;

  static readonly ReviewAndSubmit = `${this.SlidesParent}.reviewAndSubmit`;

  static readonly Referral = `${this.ReviewAndSubmit}.referral`;
  static readonly ReferralEdit = `${this.Referral}.edit`;

  static readonly Review = `${this.ReviewAndSubmit}.review`;

  static readonly Submit = `${this.ReviewAndSubmit}.submit`;
}

export const slidesState: Ng2StateDeclaration = {
  name: SlidesStateNames.SlidesParent,
  url: '/slides',
  abstract: true,
  loadComponent: () => import('../slides/slides.component').then(m => m.SlidesComponent),
};

export const gettingStartedState: Ng2StateDeclaration = {
  name: SlidesStateNames.GettingStarted,
  url: '/gettingStarted',
  loadComponent: () =>
    import('../slides/getting-started/getting-started.component').then(m => m.GettingStartedComponent),
  data: { order: 1, isValid: true },
};

export const borrowerStates: Array<Ng2StateDeclaration> = [
  {
    name: SlidesStateNames.Borrowers,
    url: '/borrowers?:borrowerId',
    component: UIView,
    redirectTo: borrowersRedirectHandler,
    params: {
      borrowerId: {
        type: 'int',
        dynamic: true,
        value: null,
        squash: true,
      },
    },
    data: { order: 2, isValid: true },
  },
  {
    name: SlidesStateNames.BorrowersEdit,
    url: '/edit',
    loadComponent: () =>
      import('../slides/borrowers/borrower-edit/borrower-edit.component').then(m => m.BorrowersEditComponent),
  },
  {
    name: SlidesStateNames.BorrowersSummary,
    url: `/summary`,
    loadComponent: () =>
      import('../slides/borrowers/borrower-summary/borrower-summary.component').then(m => m.BorrowerSummaryComponent),
  },
];

export const incomeStates: Array<Ng2StateDeclaration> = [
  {
    name: SlidesStateNames.Income,
    url: '/income',
    component: UIView,
    redirectTo: incomeRedirectHandler,
    data: { order: 3, isValid: true },
  },
  {
    name: SlidesStateNames.IncomeEdit,
    url: `/edit?{borrowerId}{employmentId}{otherIncomeId}`,
    loadComponent: () => import('../slides/income/income-edit/income-edit.component').then(m => m.IncomeEditComponent),
    params: {
      borrowerId: { dynamic: true, value: null, type: 'path' },
      employmentId: { dynamic: true, value: null },
      otherIncomeId: { dynamic: true, value: null },
    },
    data: { displayName: 'Income' },
  },
  {
    name: SlidesStateNames.IncomeSummary,
    url: '/summary',
    loadComponent: () =>
      import('../slides/income/income-summary/income-summary.component').then(m => m.IncomeSummaryComponent),
    params: {
      data: { displayName: 'Income' },
    },
  },
];

export const bankingStates: Array<Ng2StateDeclaration> = [
  {
    name: SlidesStateNames.Banking,
    url: '/banking',
    component: UIView,
    redirectTo: bankingRedirectHandler,
    data: { order: 4, isValid: true },
  },
  {
    name: SlidesStateNames.BankingEdit,
    url: '/edit?{borrowerId}{accountId}{bankId}{bankName}',
    loadComponent: () =>
      import('../slides/banking/banking-edit/banking-edit.component').then(m => m.BankingEditComponent),
  },
  {
    name: SlidesStateNames.BankingSummary,
    url: '/summary',
    loadComponent: () =>
      import('../slides/banking/banking-summary/banking-summary.component').then(m => m.BankingSummaryComponent),
  },
];

export const assetsStates: Array<Ng2StateDeclaration> = [
  {
    name: SlidesStateNames.Assets,
    url: '/assets',
    component: UIView,
    redirectTo: assetsRedirectHandler,
    data: { order: 5, isValid: true },
  },
  {
    name: SlidesStateNames.AssetsEdit,
    url: '/edit?{borrowerId}{assetId}',
    loadComponent: () => import('../slides/assets/asset-edit/asset-edit.component').then(m => m.AssetEditComponent),
  },
  {
    name: SlidesStateNames.AssetsSummary,
    url: '/summary',
    loadComponent: () =>
      import('../slides/assets/assets-summary/assets-summary.component').then(m => m.AssetsSummaryComponent),
  },
];

export const propertiesStates: Array<Ng2StateDeclaration> = [
  {
    name: SlidesStateNames.Properties,
    url: '/properties',
    component: UIView,
    redirectTo: propertiesRedirectHandler,
    data: { order: 6, isValid: true },
  },
  {
    name: SlidesStateNames.PropertiesEdit,
    url: '/edit?{borrowerId}{propertyId}{scroll}',
    loadComponent: () =>
      import('../slides/properties/property-edit/property-edit.component').then(m => m.PropertyEditComponent),
    params: {
      borrowerId: { type: 'int', dynamic: true, value: null },
      propertyId: { type: 'int', dynamic: true, value: null, squash: true },
      scroll: { dynamic: true, value: null, squash: true },
    },
  },
  {
    name: SlidesStateNames.PropertiesSummary,
    url: '/summary',
    loadComponent: () =>
      import('../slides/properties/properties-summary/properties-summary.component').then(
        m => m.PropertiesSummaryComponent,
      ),
  },
];

export const referralStates: Array<Ng2StateDeclaration> = [
  {
    name: SlidesStateNames.Referral,
    url: '/referral',
    component: UIView,
    redirectTo: referralRedirectHandler,
  },
  {
    name: SlidesStateNames.ReferralEdit,
    url: '/edit',
    loadComponent: () =>
      import('../slides/reviewAndSubmit/referral/referral-edit/referral-edit.component').then(
        m => m.ReferralEditComponent,
      ),
  },
];

export const reviewAndSubmitStates: Array<Ng2StateDeclaration> = [
  {
    name: SlidesStateNames.ReviewAndSubmit,
    url: '/reviewAndSubmit',
    component: UIView,
    redirectTo: SlidesStateNames.Referral,
    data: { order: 7, isValid: true },
  },
  {
    name: SlidesStateNames.Review,
    url: '/review',
    loadComponent: () => import('../slides/reviewAndSubmit/review/review.component').then(m => m.ReviewComponent),
  },
  {
    name: SlidesStateNames.Submit,
    url: '/submit',
    loadComponent: () => import('../slides/reviewAndSubmit/submit/submit.component').then(m => m.SubmitComponent),
  },
];

console.log('borrowerStates', borrowerStates);
export const SlidesStates: Array<Ng2StateDeclaration> = [
  slidesState,
  gettingStartedState,
  ...borrowerStates,
  ...incomeStates,
  ...bankingStates,
  ...assetsStates,
  ...propertiesStates,
  ...referralStates,
  ...reviewAndSubmitStates,
];
